// What a recognised word's chip says, in English or German, as the kit says it (DateFormatting.swift, the rule
// titles in Models.swift and TodoChoices.swift, and the kit's Localizable.xcstrings). Dates and times go through
// Intl, as the kit's go through the device's formatter; the rest are the catalog's words.

import type { Day, MonthPosition, RepeatRule, Weekday } from "../types";
import { daysBetween, noonUTC } from "./calendar";

export type LabelLanguage = "en" | "de";

export interface LabelOptions {
  language: LabelLanguage;
  /** The locale for dates and times, such as "en-US" or "de-DE". */
  locale: string;
  /** The weekday a week starts on, which orders the days of a weekly rule. */
  firstWeekday: Weekday;
}

const words = {
  en: { today: "Today", tomorrow: "Tomorrow", yesterday: "Yesterday", someday: "Someday" },
  de: { today: "Heute", tomorrow: "Morgen", yesterday: "Gestern", someday: "Irgendwann" },
} as const;

// Calendar's weekdaySymbols and shortWeekdaySymbols, Sunday first.
const weekdayNames = {
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  de: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
} as const;
const shortWeekdayNames = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  de: ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."],
} as const;

export function todayLabel(options: LabelOptions): string {
  return words[options.language].today;
}

export function somedayLabel(options: LabelOptions): string {
  return words[options.language].someday;
}

/** "Today", "Tomorrow", or a short date (Date.friendly). */
export function friendly(day: Day, today: Day, options: LabelOptions): string {
  const distance = daysBetween(today, day);
  const said = words[options.language];
  if (distance === 0) return said.today;
  if (distance === 1) return said.tomorrow;
  if (distance === -1) return said.yesterday;
  const format: Intl.DateTimeFormatOptions =
    distance >= 2 && distance <= 6
      ? { weekday: "long", timeZone: "UTC" }
      : { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" };
  return new Intl.DateTimeFormat(options.locale, format).format(noonUTC(day));
}

/** A time of day, short, as the locale writes it ("5:00 PM", "17:00"). */
export function timeLabel(hour: number, minute: number, options: LabelOptions): string {
  const time = new Date(Date.UTC(2000, 0, 1, hour, minute));
  return new Intl.DateTimeFormat(options.locale, { hour: "numeric", minute: "2-digit", timeZone: "UTC" }).format(time);
}

/** "the third Monday", "the last day" (MonthPosition.title). */
function placeTitle(position: MonthPosition, language: LabelLanguage): string {
  const day = position.weekday === undefined ? (language === "de" ? "Tag" : "day") : weekdayNames[language][position.weekday - 1];
  const ordinal = {
    en: { 1: "the first", 2: "the second", 3: "the third", 4: "the fourth", last: "the last" },
    de: { 1: "ersten", 2: "zweiten", 3: "dritten", 4: "vierten", last: "letzten" },
  }[language];
  const word = position.ordinal >= 1 && position.ordinal <= 4 ? ordinal[position.ordinal as 1 | 2 | 3 | 4] : ordinal.last;
  return `${word} ${day}`;
}

/** "15th", "1st", "22nd" in English, "15." in German. */
export function ordinalDay(day: number, language: LabelLanguage): string {
  if (language === "de") return `${day}.`;
  const suffix = day % 100 >= 11 && day % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[day % 10] ?? "th";
  return `${day}${suffix}`;
}

/**
 * "the 15th", "the 1st and 15th", "the 1st, 15th and last day"; in German "15.", "1. und 15.", "1., 15. und letzten
 * Tag", after "am" (RepeatRule.monthDaysPlace): 31 is the last day.
 */
export function monthDaysPlace(days: readonly number[], language: LabelLanguage): string {
  const names = [...days].sort((a, b) => a - b).map((day) => (day >= 31 ? (language === "de" ? "letzten Tag" : "last day") : ordinalDay(day, language)));
  const last = names[names.length - 1] ?? "";
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} ${language === "de" ? "und" : "and"} ${last}` : last;
  return language === "de" ? list : `the ${list}`;
}

/** "Every 2 weeks", "Every month", "Jeden Tag": the catalog's plural forms (RepeatRule.everyTitle). */
function everyTitle(rule: RepeatRule, language: LabelLanguage): string {
  const n = rule.interval;
  if (language === "de") {
    const one = { day: "Jeden Tag", week: "Jede Woche", month: "Jeden Monat", year: "Jedes Jahr" }[rule.unit];
    const many = { day: "Tage", week: "Wochen", month: "Monate", year: "Jahre" }[rule.unit];
    return n === 1 ? one : `Alle ${n} ${many}`;
  }
  return n === 1 ? `Every ${rule.unit}` : `Every ${n} ${rule.unit}s`;
}

function afterCompletionTitle(rule: RepeatRule, language: LabelLanguage): string {
  const n = rule.interval;
  if (language === "de") {
    const unit = {
      day: ["Tag", "Tage"],
      week: ["Woche", "Wochen"],
      month: ["Monat", "Monate"],
      year: ["Jahr", "Jahre"],
    }[rule.unit][n === 1 ? 0 : 1];
    return `${n} ${unit} nach Erledigung`;
  }
  return `${n} ${rule.unit}${n === 1 ? "" : "s"} after completion`;
}

/** A habit's number of times: "3 times a week", "Once a month" (HabitSchedule.title). */
export function habitTitle(schedule: { kind: string; count?: number }, options: LabelOptions): string {
  const count = Math.max(schedule.count ?? 1, 1);
  const week = schedule.kind === "timesPerWeek";
  if (options.language === "de") {
    if (count === 1) return week ? "Einmal pro Woche" : "Einmal im Monat";
    return week ? `${count}-mal pro Woche` : `${count}-mal im Monat`;
  }
  if (count === 1) return week ? "Once a week" : "Once a month";
  return week ? `${count} times a week` : `${count} times a month`;
}

/** The rule in words, with its end: "Every week on Mon, until 1 Dec" (RepeatRule.title). */
export function repeatTitle(rule: RepeatRule, options: LabelOptions): string {
  const { language } = options;
  const title = ruleTitle(rule, options);
  if (rule.endsOn === undefined) return title;
  const end = new Intl.DateTimeFormat(options.locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(
    noonUTC(rule.endsOn),
  );
  return language === "de" ? `${title}, bis ${end}` : `${title}, until ${end}`;
}

function ruleTitle(rule: RepeatRule, options: LabelOptions): string {
  const { language } = options;
  const n = rule.interval;
  if (rule.unit === "month" && rule.monthPosition) {
    const place = placeTitle(rule.monthPosition, language);
    if (rule.afterCompletion) {
      if (language === "de") return `${n} ${n === 1 ? "Monat" : "Monate"} nach Erledigung, am ${place}`;
      return `${n} ${n === 1 ? "month" : "months"} after completion, on ${place}`;
    }
    if (language === "de") return n === 1 ? `Jeden Monat am ${place}` : `Alle ${n} Monate am ${place}`;
    return n === 1 ? `Every month on ${place}` : `Every ${n} months on ${place}`;
  }
  const monthDays = rule.unit === "month" && !rule.monthPosition && rule.monthDays
    ? [...new Set(rule.monthDays.filter((day) => Number.isInteger(day) && day >= 1 && day <= 31))] : undefined;
  if (monthDays && monthDays.length > 0) {
    const place = monthDaysPlace(monthDays, language);
    if (rule.afterCompletion) {
      if (language === "de") return `${n} ${n === 1 ? "Monat" : "Monate"} nach Erledigung, am ${place}`;
      return `${n} ${n === 1 ? "month" : "months"} after completion, on ${place}`;
    }
    if (language === "de") return n === 1 ? `Jeden Monat am ${place}` : `Alle ${n} Monate am ${place}`;
    return n === 1 ? `Every month on ${place}` : `Every ${n} months on ${place}`;
  }
  const days = rule.unit === "week" && rule.weekdays && rule.weekdays.length > 0 ? rule.weekdays : undefined;
  if (days) {
    const workdays = [2, 3, 4, 5, 6];
    const isWorkdays = days.length === workdays.length && workdays.every((day) => days.includes(day as Weekday));
    if (n === 1 && isWorkdays) return language === "de" ? "Jeden Wochentag" : "Every weekday";
    const ordered: number[] = [];
    for (let i = 0; i < 7; i++) {
      const day = ((options.firstWeekday - 1 + i) % 7) + 1;
      if (days.includes(day as Weekday)) ordered.push(day);
    }
    const names = ordered.map((day) => shortWeekdayNames[language][day - 1]).join(", ");
    if (language === "de") return n === 1 ? `Jede Woche am ${names}` : `Alle ${n} Wochen am ${names}`;
    return n === 1 ? `Every week on ${names}` : `Every ${n} weeks on ${names}`;
  }
  return rule.afterCompletion ? afterCompletionTitle(rule, language) : everyTitle(rule, language);
}
